import {
	SketchBezierCurveClosureKind,
	SketchBezierCurveKind,
	type SketchSplineObject,
} from '../../../../mesh/sketch/SketchSplineObject';
import type {SplineInterpolationPointLike} from '../../../../tool/normalizeSplineInterpolationPoints';
import {
	resolvePeriodicSplineControlPoints,
	resolveUniqueClosedSplineInterpolationPoints,
} from '../../../../tool/sampleClosedSplineInterpolationPoints';

/** 转换实体闭合普通样条统一使用三次 B 样条表达。 */
const REFERENCE_PERIODIC_SPLINE_DEGREE = 3;

/**
 * 转换实体普通样条精确求值定义解析器。
 *
 * 说明：
 * 1. 来源对象已有权威定义时直接读取；
 * 2. 两拟合点与闭合周期样条可由来源拟合点无损恢复，作为转换实体专用兜底；
 * 3. 开放高阶插值样条缺少权威定义时返回 null，禁止使用显示折线代替。
 */
export class SketchSplineReferenceEvaluationDefinitionResolver {
	/**
	 * 解析转换实体参数求解使用的普通样条精确定义。
	 *
	 * @param sourceSpline 转换实体的来源普通样条。
	 * @returns 可无损求值的 B 样条定义；来源无法精确恢复时返回 null。
	 */
	public resolve(sourceSpline: SketchSplineObject): SketchBSplineEvaluationDefinition | null {
		const curveMetadata = sourceSpline.getCurveMetadata();
		// SketchSplineObject 同时承载 Bezier/B-Spline 变体，该兜底仅适用于普通插值样条。
		if (curveMetadata.curveKind !== SketchBezierCurveKind.Spline) {
			return null;
		}

		const existingDefinition = sourceSpline.getCurveEvaluationDefinition();
		if (existingDefinition) {
			return existingDefinition;
		}

		const interpolationPoints = sourceSpline.getControlPoints()
			.map(point => point.getPosition());
		if (curveMetadata.closureKind === SketchBezierCurveClosureKind.Closed) {
			return this.createPeriodicDefinition(interpolationPoints);
		}

		// 两拟合点普通样条在产品中就是有限线段，一次 B 样条可无损表达全部参数语义。
		if (interpolationPoints.length === 2) {
			return this.createLinearDefinition(interpolationPoints);
		}

		// 开放高阶样条的内核极点不能由显示采样或拟合点推测。
		return null;
	}

	/**
	 * 创建两拟合点普通样条的一次 B 样条定义。
	 *
	 * @param interpolationPoints 两个有序拟合点。
	 * @returns 与两点线段完全等价的开放 B 样条定义。
	 */
	private createLinearDefinition(
		interpolationPoints: readonly SplineInterpolationPointLike[],
	): SketchBSplineEvaluationDefinition {
		return {
			controlPoints: interpolationPoints.map(point => ({
				x: point.x,
				y: point.y,
				z: point.z,
			})),
			weights: [1, 1],
			degree: 1,
			knots: [0, 0, 1, 1],
			firstParameter: 0,
			lastParameter: 1,
			periodic: false,
		};
	}

	/**
	 * 创建闭合普通样条的周期三次 B 样条定义。
	 *
	 * @param interpolationPoints 闭合样条拟合点，允许末点重复首点。
	 * @returns 至少三个唯一拟合点时返回精确定义，否则返回 null。
	 */
	private createPeriodicDefinition(
		interpolationPoints: readonly SplineInterpolationPointLike[],
	): SketchBSplineEvaluationDefinition | null {
		const uniquePoints = resolveUniqueClosedSplineInterpolationPoints(interpolationPoints);
		if (uniquePoints.length < 3) {
			return null;
		}

		const periodicControlPoints = resolvePeriodicSplineControlPoints(uniquePoints);
		const periodicPointCount = periodicControlPoints.length;
		/**
		 * 主线程 De Boor 求值器消费普通完整节点向量，因此将周期极点展开一个完整参数周期：
		 * `P[-1], P[0] ... P[n-1], P[0], P[1]`。每个有效节点区间由此与正式周期采样的四极点窗口一一对应。
		 */
		const expandedControlPoints = Array.from(
			{length: periodicPointCount + REFERENCE_PERIODIC_SPLINE_DEGREE},
			(_unused, index) => {
				const point = this.readPeriodicPoint(periodicControlPoints, index - 1);
				return {x: point.x, y: point.y, z: point.z};
			},
		);
		const knotCount = expandedControlPoints.length + REFERENCE_PERIODIC_SPLINE_DEGREE + 1;
		const knots = Array.from({length: knotCount}, (_unused, index) => index);
		const firstParameter = REFERENCE_PERIODIC_SPLINE_DEGREE;

		return {
			controlPoints: expandedControlPoints,
			weights: new Array(expandedControlPoints.length).fill(1),
			degree: REFERENCE_PERIODIC_SPLINE_DEGREE,
			knots,
			firstParameter,
			lastParameter: firstParameter + periodicPointCount,
			periodic: true,
		};
	}

	/**
	 * 按周期索引读取控制点。
	 *
	 * @param controlPoints 一个周期内的控制点。
	 * @param index 允许越界的原始索引。
	 * @returns 归一化到当前周期后的控制点。
	 */
	private readPeriodicPoint(
		controlPoints: readonly SplineInterpolationPointLike[],
		index: number,
	): SplineInterpolationPointLike {
		const normalizedIndex = ((index % controlPoints.length) + controlPoints.length)
			% controlPoints.length;
		return controlPoints[normalizedIndex]!;
	}
}
